import { IBoardSnapshot } from '../../../dtos/IBoardSnapshot';

export type TConnectionStatus = 'connecting' | 'live' | 'polling' | 'offline';

/** Falhas seguidas do SSE antes de cair para o polling. */
export const SSE_MAX_FAILURES = 3;
export const POLL_INTERVAL_MS = 5_000;
/** Com o polling ligado, tenta voltar ao SSE a cada minuto. */
export const SSE_RETRY_MS = 60_000;
/** Sem heartbeat (ou resposta do polling) por esse tempo = "sem conexão". */
export const STALE_AFTER_MS = 45_000;
const WATCHDOG_MS = 5_000;

interface IEventSourceLike {
  /** 2 = CLOSED: o navegador desistiu (ex.: 503/404) e não reconecta sozinho. */
  readyState?: number;
  onopen: ((ev: Event) => void) | null;
  onerror: ((ev: Event) => void) | null;
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void;
  close(): void;
}

export interface IBoardConnectionOptions {
  baseUrl: string;
  token: string;
  onSnapshot: (snapshot: IBoardSnapshot) => void;
  onStatus: (status: TConnectionStatus) => void;
  onNotFound?: () => void;
  createEventSource?: (url: string) => IEventSourceLike;
  fetchFn?: typeof fetch;
}

/**
 * Mantém o telão atualizado o dia todo:
 *
 * - SSE como caminho principal. O próprio EventSource reconecta; contamos as
 *   falhas e, depois de 3 seguidas (ou um 503 de lotação), caímos para
 *   polling de 5s com ETag (304 = nada mudou).
 * - Com polling ligado, a cada minuto tenta o SSE de novo; se abrir, o
 *   polling para.
 * - Descarta versões menores ou iguais à atual (reconexão, outra instância).
 * - Sem sinal de vida por 45s, avisa "offline" — a tela continua mostrando
 *   o último estado.
 */
export default class BoardConnection {
  private version = 0;

  private source: IEventSourceLike | null = null;

  private failures = 0;

  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  private watchdog: ReturnType<typeof setInterval> | null = null;

  private lastSignal = Date.now();

  private status: TConnectionStatus = 'connecting';

  private stopped = false;

  constructor(private options: IBoardConnectionOptions) {}

  private get boardUrl(): string {
    return `${this.options.baseUrl}/public/boards/${this.options.token}`;
  }

  public start(): void {
    this.stopped = false;
    this.lastSignal = Date.now();
    this.setStatus('connecting');
    this.openStream();

    this.watchdog = setInterval(() => {
      if (Date.now() - this.lastSignal > STALE_AFTER_MS) {
        this.setStatus('offline');
      }
    }, WATCHDOG_MS);
  }

  public stop(): void {
    this.stopped = true;
    this.closeStream();
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.watchdog) clearInterval(this.watchdog);
    this.pollTimer = null;
    this.retryTimer = null;
    this.watchdog = null;
  }

  public get currentVersion(): number {
    return this.version;
  }

  private openStream(): void {
    const create =
      this.options.createEventSource ??
      ((url: string) => new EventSource(url) as unknown as IEventSourceLike);

    const source = create(`${this.boardUrl}/stream`);
    this.source = source;

    source.onopen = () => {
      this.failures = 0;
      this.signal();
      this.stopPolling();
      this.setStatus('live');
    };

    source.addEventListener('board', event => {
      this.signal();
      this.receive(event.data);
    });

    source.addEventListener('ping', () => this.signal());

    source.onerror = () => {
      if (this.source !== source) return;
      this.failures += 1;

      const closedForGood = source.readyState === 2;

      if (closedForGood || this.failures >= SSE_MAX_FAILURES) {
        this.closeStream();
        this.startPolling();
      }
    };
  }

  private closeStream(): void {
    this.source?.close();
    this.source = null;
  }

  private startPolling(): void {
    if (this.stopped || this.pollTimer) return;

    this.setStatus('polling');
    this.poll();
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);

    this.retryTimer = setTimeout(() => this.retryStream(), SSE_RETRY_MS);
  }

  private stopPolling(): void {
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.pollTimer = null;
    this.retryTimer = null;
  }

  private retryStream(): void {
    this.retryTimer = null;
    if (this.stopped || this.source) return;

    this.failures = SSE_MAX_FAILURES - 1; // uma falha já volta ao polling
    this.openStream();

    if (this.pollTimer) {
      this.retryTimer = setTimeout(() => this.retryStream(), SSE_RETRY_MS);
    }
  }

  private async poll(): Promise<void> {
    const fetchFn = this.options.fetchFn ?? fetch.bind(window);

    try {
      const response = await fetchFn(this.boardUrl, {
        headers: this.version ? { 'If-None-Match': `"${this.version}"` } : {},
        cache: 'no-cache',
      });

      if (this.stopped) return;

      if (response.status === 404) {
        this.options.onNotFound?.();
        return;
      }

      if (response.status === 304) {
        this.signal();
        return;
      }

      if (response.ok) {
        this.signal();
        this.receive(await response.text());
        if (this.status !== 'live') this.setStatus('polling');
      }
    } catch {
      // Rede caiu: o watchdog marca "offline" se continuar assim.
    }
  }

  private receive(raw: string): void {
    let snapshot: IBoardSnapshot;
    try {
      snapshot = JSON.parse(raw);
    } catch {
      return;
    }

    if (!snapshot || snapshot.version <= this.version) return;

    this.version = snapshot.version;
    this.options.onSnapshot(snapshot);
  }

  private signal(): void {
    this.lastSignal = Date.now();
    if (this.status === 'offline') {
      this.setStatus(this.source && !this.pollTimer ? 'live' : 'polling');
    }
  }

  private setStatus(status: TConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus(status);
  }
}
